import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// Policy text is taken from https://www.janmabhoominewspapers.com/policies/*
// and https://www.janmabhoominewspapers.com/contact. Keep in sync with that site.

const SUPPORT_EMAIL = "jbhoomi@gmail.com";

function PolicyLayout({ title, updated, children }) {
  const navigate = useNavigate();
  return (
    <div className="page-wrap">
      <Navbar onLoginClick={() => navigate("/")} />
      <main className="policy-wrap">
        <h1>{title}</h1>
        {updated && <p className="policy-updated">{updated}</p>}
        {children}
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="policy-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/* ── Privacy Policy ─────────────────────────────────────────────────────── */
export function PrivacyPage() {
  return (
    <PolicyLayout title="Privacy Policy" updated="Last Updated - 24th August, 2026">
      <p>
        This Privacy Policy explains how the Janmabhoomi mobile application ("App", "we", "us"),
        owned and operated by Saurashtra Trust, collects, uses, stores, shares and protects your
        information. It applies to your use of the App on your mobile device and to the JB+
        subscription service offered within it. Your privacy is important to us, and it guides how
        we design and run every feature of the App.
      </p>

      <Section title="1. Information We Collect">
        <p>What we collect depends on how you use the App:</p>
        <ul>
          <li><strong>Account information</strong> — Guest access requires no personal information. Signing up or logging in via email and password, Google Sign-In, or phone number (OTP) shares your name, email address, or phone number with us, as applicable.</li>
          <li><strong>Location</strong> — with your permission, we use your device's approximate (coarse) location to suggest your city/region when editing your profile. We do not store your precise coordinates — only the resulting city name.</li>
          <li><strong>Preferences and app activity</strong> — your selected language, edition(s), topics of interest, notification preferences, bookmarked/saved articles, and display settings (text size, theme).</li>
          <li><strong>Device and usage data</strong> — device identifiers, operating system and app version, and diagnostic/crash data, collected through crash-reporting tools so we can fix issues and improve the App.</li>
          <li><strong>Push notification data</strong> — a device notification token, used to deliver breaking news alerts and the topic- and edition-based notifications you have opted into.</li>
          <li><strong>Subscription and payment data</strong> — if you subscribe to JB+, our payment partner, Razorpay, processes your payment. We do not collect or store your card, UPI, or bank account details — we only receive your subscription and payment status.</li>
        </ul>
      </Section>

      <Section title="2. Purpose of Collection">
        <p>
          Before or at the time we collect personal information, we identify the purpose for which
          it is being collected — such as creating your account, personalising your news feed,
          delivering the notifications you've chosen, enabling offline ePaper downloads, activating
          a JB+ subscription, or improving app performance and reliability.
        </p>
      </Section>

      <Section title="3. How We Use Your Information">
        <p>
          We use personal information only to fulfil the purposes identified at the time of
          collection, unless you separately consent or we are required to act otherwise by law. We
          do not sell your personal information.
        </p>
      </Section>

      <Section title="4. Retention">
        <p>
          We retain personal information only for as long as necessary to fulfil the purposes
          described in this policy, including any legal, accounting, or reporting obligations. If
          you delete your account, we delete or anonymise your account data, subject to any
          retention required by law or legitimate business need (for example, records of a
          completed transaction).
        </p>
      </Section>

      <Section title="5. How We Collect Information">
        <p>
          We collect information by lawful and fair means, and wherever appropriate, with your
          knowledge or consent — for example, through the onboarding screens where you choose your
          language, edition and topics, or through the permission prompts your device shows before
          we can access location, send notifications, or save files for offline reading.
        </p>
      </Section>

      <Section title="6. Data Accuracy">
        <p>
          We take reasonable steps to keep the personal information we hold accurate, complete and
          up to date, and relevant to the purpose for which it is used. You can review and edit
          your account details at any time from the Profile section of the App.
        </p>
      </Section>

      <Section title="7. Security Safeguards">
        <p>
          We use reasonable technical and organisational safeguards — including secure,
          access-controlled cloud infrastructure and encrypted transmission — to protect your
          information against unauthorised access, loss, misuse, disclosure, alteration or
          destruction. No method of transmission or storage is completely secure, and we cannot
          guarantee absolute security.
        </p>
      </Section>

      <Section title="8. Your Choices and Controls">
        <ul>
          <li><strong>Guest mode</strong> — browse the App anonymously via Guest access, without creating an account.</li>
          <li><strong>Notification preferences</strong> — turn notifications on or off, or fine-tune them by topic and edition, from Settings.</li>
          <li><strong>Device permissions</strong> — manage permissions such as location, notifications, and storage access from your device's system settings at any time.</li>
          <li><strong>Delete your account</strong> — open the app menu and select "Delete my data" to permanently delete your account and associated data directly within the App, without needing to contact support.</li>
          <li><strong>Other requests</strong> — to request access to or correction of your account data, or for any question about your data that isn't covered by the in-app deletion option, contact us using the details in Section 13.</li>
        </ul>
      </Section>

      <Section title="9. Third-Party Services">
        <p>
          The App relies on trusted third-party service providers to operate core features, each
          governed by its own privacy policy:
        </p>
        <ul>
          <li>Firebase / Google — for account sign-in, push notifications, and crash reporting.</li>
          <li>Google — for Google Sign-In authentication.</li>
          <li>Razorpay — for processing JB+ subscription payments securely.</li>
          <li>Cloudflare — for secure content delivery of images, ePaper editions, and app assets.</li>
        </ul>
        <p>
          We encourage you to review these providers' privacy policies to understand how they
          handle information on our behalf.
        </p>
      </Section>

      <Section title="10. Transparency">
        <p>
          We will always make information about our policies and practices for managing personal
          information readily available to you, and we are committed to conducting our business in
          accordance with these principles in order to protect the confidentiality and security of
          your personal information.
        </p>
      </Section>

      <Section title="11. Children's Privacy">
        <p>
          The App is intended for a general news-reading audience and is not directed at children.
          We do not knowingly collect personal information from children without appropriate
          consent as required by applicable law. If you believe a child has provided us with
          personal information, please contact us so we can take appropriate action.
        </p>
      </Section>

      <Section title="12. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time to reflect changes in the App, our
          practices, or legal requirements. We will post the updated policy within the App and
          update the effective date above. Continued use of the App after an update constitutes
          acceptance of the revised policy.
        </p>
      </Section>

      <Section title="13. Contact Us">
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or your
          personal information, please contact Saurashtra Trust / Janmabhoomi at{" "}
          <a className="policy-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </Section>
    </PolicyLayout>
  );
}

/* ── Terms of Use ───────────────────────────────────────────────────────── */
export function TermsPage() {
  const G = "Janmabhoomi Group of Newspapers";
  return (
    <PolicyLayout title="Terms of Use">
      <Section title="1. Terms">
        <p>
          By accessing this web site, you are agreeing to be bound by these web site Terms and
          Conditions of Use, all applicable laws and regulations, and agree that you are
          responsible for compliance with any applicable local laws. If you do not agree with any
          of these terms, you are prohibited from using or accessing this site. The materials
          contained in this web site are protected by applicable copyright and trade mark law.
        </p>
      </Section>

      <Section title="2. Use License">
        <p>
          Permission is granted to temporarily download one copy of the materials (information or
          software) on {G}'s web site for personal, non-commercial transitory viewing only. This
          is the grant of a license, not a transfer of title, and under this license you may not:
        </p>
        <ul>
          <li>modify or copy the materials;</li>
          <li>use the materials for any commercial purpose, or for any public display (commercial or non-commercial);</li>
          <li>attempt to decompile or reverse engineer any software contained on {G}'s web site;</li>
          <li>remove any copyright or other proprietary notations from the materials; or</li>
          <li>transfer the materials to another person or "mirror" the materials on any other server.</li>
        </ul>
        <p>
          This license shall automatically terminate if you violate any of these restrictions and
          may be terminated by {G} at any time. Upon terminating your viewing of these materials
          or upon the termination of this license, you must destroy any downloaded materials in
          your possession whether in electronic or printed format.
        </p>
      </Section>

      <Section title="3. Disclaimer">
        <p>
          The materials on {G}'s web site are provided "as is". {G} makes no warranties, expressed
          or implied, and hereby disclaims and negates all other warranties, including without
          limitation, implied warranties or conditions of merchantability, fitness for a
          particular purpose, or non-infringement of intellectual property or other violation of
          rights. Further, {G} does not warrant or make any representations concerning the
          accuracy, likely results, or reliability of the use of the materials on its Internet web
          site or otherwise relating to such materials or on any sites linked to this site.
        </p>
      </Section>

      <Section title="4. Limitations">
        <p>
          In no event shall {G} or its suppliers be liable for any damages (including, without
          limitation, damages for loss of data or profit, or due to business interruption,)
          arising out of the use or inability to use the materials on {G}'s Internet site, even if
          {" "}{G} or a {G} authorized representative has been notified orally or in writing of the
          possibility of such damage. Because some jurisdictions do not allow limitations on
          implied warranties, or limitations of liability for consequential or incidental damages,
          these limitations may not apply to you.
        </p>
      </Section>

      <Section title="5. Revisions and Errata">
        <p>
          The materials appearing on {G}'s web site could include technical, typographical, or
          photographic errors. {G} does not warrant that any of the materials on its web site are
          accurate, complete, or current. {G} may make changes to the materials contained on its
          web site at any time without notice. {G} does not, however, make any commitment to
          update the materials.
        </p>
      </Section>

      <Section title="6. Links">
        <p>
          {G} has not reviewed all of the sites linked to its Internet web site and is not
          responsible for the contents of any such linked site. The inclusion of any link does not
          imply endorsement by {G} of the site. Use of any such linked web site is at the user's
          own risk.
        </p>
      </Section>

      <Section title="7. Site Terms of Use Modifications">
        <p>
          {G} may revise these terms of use for its web site at any time without notice. By using
          this web site you are agreeing to be bound by the then current version of these Terms
          and Conditions of Use.
        </p>
      </Section>

      <Section title="8. Governing Law">
        <p>
          Any claim relating to {G}'s web site shall be governed by the laws of the State of India
          without regard to its conflict of law provisions. General Terms and Conditions
          applicable to Use of a Web Site.
        </p>
      </Section>
    </PolicyLayout>
  );
}

/* ── Refund / Cancellation Policy ───────────────────────────────────────── */
export function RefundPage() {
  return (
    <PolicyLayout title="Refund / Cancellation Policy">
      <p>
        Customer confidence and satisfaction is our primary priority. We want to build a positive
        relationship with you, and your satisfaction is very important to us.
      </p>
      <p>
        Janmabhoomi Group does not guarantee a full refund for the cancellation of its products.
        If you change your mind after paying for a certain product, Janmabhoomi Group will act
        only on the policy here within. Janmabhoomi Group will refund or cancel any products
        according to the following guidelines:
      </p>
      <ol type="a">
        <li>In case of any cancellation of order within 3 working days of making the payment and goods has not been shipped, Janmabhoomi Group will ensure 95% refund within 15 working days.</li>
        <li>If you have made full payment for an order and we are unable to ship the goods for any reason, Janmabhoomi Group will ensure 100% refund within a maximum of 15 working days.</li>
        <li>If you want to cancel the order and the material already booked for dispatch and shipment is ready at works, Janmabhoomi Group will ensure 75% refund within 15 working days.</li>
        <li>Once the goods are dispatched from our works, then cancellation of order will not be entertained.</li>
      </ol>
      <p>Please understand that we cannot refund shipping or handling costs.</p>
      <p>
        All refund transactions will be handed over by Cheque only; no refund will be processed
        either by online transfer or paid by cash.
      </p>
      <p>
        If you don't want to continue using any of Janmabhoomi Group products and wish to cancel
        the order or have any questions, comments or concerns about our refund and cancellation
        policy, you may contact us at{" "}
        <a className="policy-link" href={`mailto:${SUPPORT_EMAIL}?subject=CANCEL%20ORDER`}>{SUPPORT_EMAIL}</a>.
        Be sure to include your full name, company name, account number and the service you want
        to be cancelled. Include "CANCEL ORDER" in the subject line. *All refunds and cancellation
        requests must be made in writing. Your request will not be considered valid unless and
        until you receive confirmation from our billing department. The confirmation email or
        number given by the billing department of Janmabhoomi Group will act as a proof of the
        cancellation of service.
      </p>
      <p>
        We strongly believe that our products will be beneficial to your business. We always
        strive to make your internet experience more enjoyable and we do our best for the same.
      </p>
      <p>
        * Janmabhoomi Group reserves the right to modify any provisions of the refund and
        cancellation policy without any notice at any time.
      </p>
    </PolicyLayout>
  );
}

/* ── Contact ────────────────────────────────────────────────────────────── */
export function ContactPage() {
  return (
    <PolicyLayout title="Contact Us">
      <Section title="Address">
        <p>
          Saurashtra Trust / Janmabhoomi<br />
          Janmabhoomi Bhavan, Janmabhoomi Marg,<br />
          Fort, Mumbai - 400001.
        </p>
      </Section>
      <Section title="Email">
        <p>
          <a className="policy-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </Section>
    </PolicyLayout>
  );
}
